// ===========================================
// Academic Year Tab
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel,
  Button,
  Stack,
  Typography,
  Alert,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  useAcademicYear,
  useUpdateAcademicYear,
} from '../../../hooks/useAdminSettings';
import type { Hemisphere } from '../../../types/adminSettings.types';
import SettingsWriteGate from './SettingsWriteGate';

// ===========================================
// CONSTANTS
// ===========================================

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const HEMISPHERE_INFO = {
  southern: {
    label: 'Southern Hemisphere',
    description: 'Academic year starts in January/February',
    defaultMonth: 1,
    examples: 'Australia, New Zealand, South Africa, Brazil',
  },
  northern: {
    label: 'Northern Hemisphere',
    description: 'Academic year starts in August/September',
    defaultMonth: 8,
    examples: 'USA, UK, Europe, Canada',
  },
  custom: {
    label: 'Custom',
    description: 'Choose your own start month',
    defaultMonth: 1,
    examples: '',
  },
};

// ===========================================
// TYPES
// ===========================================

interface AcademicYearFormData {
  hemisphere: Hemisphere;
  startMonth: number;
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function AcademicYearTab() {
  const { data: settings, isLoading, error } = useAcademicYear();
  const updateMutation = useUpdateAcademicYear();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<AcademicYearFormData>({
    defaultValues: {
      hemisphere: 'southern',
      startMonth: 1,
    },
  });

  const hemisphere = watch('hemisphere');

  // Update form when data loads
  useEffect(() => {
    if (settings) {
      reset({
        hemisphere: settings.hemisphere,
        startMonth: settings.startMonth,
      });
    }
  }, [settings, reset]);

  // Auto-set month when hemisphere changes (except custom)
  useEffect(() => {
    if (hemisphere !== 'custom') {
      setValue('startMonth', HEMISPHERE_INFO[hemisphere].defaultMonth);
    }
  }, [hemisphere, setValue]);

  const onSubmit = (data: AcademicYearFormData) => {
    updateMutation.mutate({
      hemisphere: data.hemisphere,
      startMonth: data.startMonth,
    });
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load academic year settings. Please try again.
      </Alert>
    );
  }

  const selectedInfo = HEMISPHERE_INFO[hemisphere];

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardHeader
            title="Academic Year Configuration"
            subheader="Set when your academic year begins for term planning and reporting"
          />
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="hemisphere"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel>Region</FormLabel>
                    <RadioGroup {...field}>
                      {(Object.keys(HEMISPHERE_INFO) as Hemisphere[]).map((key) => (
                        <FormControlLabel
                          key={key}
                          value={key}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                {HEMISPHERE_INFO[key].label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {HEMISPHERE_INFO[key].description}
                                {HEMISPHERE_INFO[key].examples && (
                                  <> ({HEMISPHERE_INFO[key].examples})</>
                                )}
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1, alignItems: 'flex-start' }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              />

              <Divider />

              <Controller
                name="startMonth"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ maxWidth: 250 }} disabled={hemisphere !== 'custom'}>
                    <InputLabel>Academic Year Start Month</InputLabel>
                    <Select
                      {...field}
                      label="Academic Year Start Month"
                    >
                      {MONTHS.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Alert severity="info" icon={false}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Setting: {selectedInfo.label}
                </Typography>
                <Typography variant="body2">
                  Your academic year starts in <strong>{MONTHS.find(m => m.value === watch('startMonth'))?.label}</strong>.
                  This affects:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                  <li>Default term date suggestions</li>
                  <li>Year-over-year reporting periods</li>
                  <li>Student age group transitions</li>
                </ul>
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <SettingsWriteGate>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}
